/**
 * Report periods (spec D §1): calendar-day arithmetic on `YYYY-MM-DD` keys,
 * shared by the API (which computes the comparison window) and the page
 * (which steps between periods). Keys are org-zone calendar days; the maths
 * runs at UTC noon so no offset or DST change can move a date. Imports come
 * from siblings only, never `./index` (see expected-day.ts).
 */
export type PeriodKind = "month" | "quarter" | "custom";
export interface PeriodKeys {
  from: string;
  to: string;
}

const DAY_MS = 86_400_000;
const noon = (key: string) => new Date(`${key}T12:00:00.000Z`);
const keyOf = (d: Date) => d.toISOString().slice(0, 10);
const parts = (key: string) => {
  const [y, m, d] = key.split("-").map(Number);
  return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 };
};
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number) => {
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return keyOf(dt);
};

export function addDays(key: string, n: number): string {
  return keyOf(new Date(noon(key).getTime() + n * DAY_MS));
}

export function daysInclusive(from: string, to: string): number {
  return Math.round((noon(to).getTime() - noon(from).getTime()) / DAY_MS) + 1;
}

export function dateKeysBetween(from: string, to: string): string[] {
  const n = daysInclusive(from, to);
  return Array.from({ length: Math.max(0, n) }, (_, i) => addDays(from, i));
}

export function monthRange(key: string): PeriodKeys {
  const { y, m } = parts(key);
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(lastDay(y, m))}` };
}

export function quarterRange(key: string): PeriodKeys {
  const { y, m } = parts(key);
  const first = m - ((m - 1) % 3);
  return { from: `${y}-${pad(first)}-01`, to: monthRange(`${y}-${pad(first + 2)}-01`).to };
}

export function periodKind(from: string, to: string): PeriodKind {
  const month = monthRange(from);
  if (month.from === from && month.to === to) return "month";
  const quarter = quarterRange(from);
  if (quarter.from === from && quarter.to === to) return "quarter";
  return "custom";
}

export function previousPeriod(from: string, to: string): PeriodKeys {
  return shiftPeriod(from, to, -1);
}

export function shiftPeriod(from: string, to: string, dir: 1 | -1): PeriodKeys {
  const kind = periodKind(from, to);
  const { y, m } = parts(from);
  if (kind === "month") return monthRange(ymd(y, m + dir, 1));
  if (kind === "quarter") return quarterRange(ymd(y, m + 3 * dir, 1));
  const len = daysInclusive(from, to);
  return dir === 1
    ? { from: addDays(to, 1), to: addDays(to, len) }
    : { from: addDays(from, -len), to: addDays(from, -1) };
}

export function lastCompleteMonth(todayKey: string): PeriodKeys {
  const { y, m } = parts(todayKey);
  return monthRange(ymd(y, m - 1, 1));
}

const fmt = (key: string, opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("fr-FR", { timeZone: "UTC", ...opts }).format(noon(key));

export function periodLabel(from: string, to: string): string {
  const kind = periodKind(from, to);
  if (kind === "month") return fmt(from, { month: "long", year: "numeric" });
  if (kind === "quarter") return `T${Math.floor((parts(from).m - 1) / 3) + 1} ${parts(from).y}`;
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const short = { day: "numeric", month: "short" } as const;
  return sameYear
    ? `${fmt(from, short)} – ${fmt(to, { ...short, year: "numeric" })}`
    : `${fmt(from, { ...short, year: "numeric" })} – ${fmt(to, { ...short, year: "numeric" })}`;
}
